import { primitives } from "@/styles/nawy-theme/foundation/colors/primitives";
import { withOpacity, getPrimitiveColor } from "@/styles/nawy-theme/utils";

export type ColorValue = string | undefined;

type SemanticBase = {
  primary: ColorValue;
  secondary: ColorValue;
};

type ExtensionColors = {
  positive: ColorValue;
  warning: ColorValue;
  negative: ColorValue;
  disabled: ColorValue;
};

type ExtensionColorsWithLight = ExtensionColors & {
  positiveLight: ColorValue;
  warningLight: ColorValue;
  negativeLight: ColorValue;
  disabledLight: ColorValue;
};

type ColorPalette = {
  grey: ColorValue;
  violet: ColorValue;
  magenta: ColorValue;
  navy: ColorValue;
  blue: ColorValue;
  cyan: ColorValue;
  green: ColorValue;
  lime: ColorValue;
  yellow: ColorValue;
  orange: ColorValue;
  red: ColorValue;
  greyLight: ColorValue;
  violetLight: ColorValue;
  magentaLight: ColorValue;
  navyLight: ColorValue;
  blueLight: ColorValue;
  cyanLight: ColorValue;
  greenLight: ColorValue;
  limeLight: ColorValue;
  yellowLight: ColorValue;
  orangeLight: ColorValue;
  redLight: ColorValue;
  greyDark: ColorValue;
  violetDark: ColorValue;
  magentaDark: ColorValue;
  navyDark: ColorValue;
  blueDark: ColorValue;
  cyanDark: ColorValue;
  greenDark: ColorValue;
  limeDark: ColorValue;
  yellowDark: ColorValue;
  orangeDark: ColorValue;
  redDark: ColorValue;
};

export type SemanticState = {
  light08: ColorValue;
  light12: ColorValue;
  light16: ColorValue;
  onColor08: ColorValue;
  onColor12: ColorValue;
  onColor16: ColorValue;
  onDark08: ColorValue;
  onDark12: ColorValue;
  onDark16: ColorValue;
};

export type SemanticText = SemanticBase & {
  tertiary: ColorValue;
  onColor: ColorValue;
  accent: ColorValue;
  extension: ExtensionColors;
};

type SemanticLayer = SemanticBase & {
  secondaryDark: ColorValue;
  tertiary: ColorValue;
  tertiaryDark: ColorValue;
  primaryInverse: ColorValue;
  accentDark: ColorValue;
  accent: ColorValue;
  accentLight: ColorValue;
  extension: ExtensionColorsWithLight;
};

type SemanticBorder = SemanticBase & {
  primaryDark: ColorValue;
  primaryInverse: ColorValue;
  accent: ColorValue;
  accentLight: ColorValue;
  extension: ExtensionColors;
};

type SemanticIndicator = {
  onColor: ColorValue;
} & ColorPalette;

type SemanticOverlay = {
  primary60: ColorValue;
};

export interface Semantic {
  text: SemanticText;
  layer: SemanticLayer;
  border: SemanticBorder;
  indicator: SemanticIndicator;
  state: SemanticState;
  overlay: SemanticOverlay;
}

export const semantic: Semantic = {
  text: {
    primary: primitives.grey[900],
    secondary: primitives.grey[700],
    tertiary: primitives.grey[600],
    onColor: primitives.white[100],
    accent: primitives.blue[500],
    extension: {
      positive: primitives.green[700],
      warning: primitives.orange[700],
      negative: primitives.red[700],
      disabled: primitives.grey[500],
    },
  },
  layer: {
    primary: primitives.white[100],
    secondary: primitives.navy[50],
    secondaryDark: primitives.navy[400],
    tertiary: primitives.navy[100],
    tertiaryDark: primitives.navy[500],
    primaryInverse: primitives.grey[900],
    accentDark: primitives.blue[900],
    accent: primitives.blue[500],
    accentLight: primitives.blue[50],
    extension: {
      positive: primitives.green[700],
      positiveLight: primitives.green[50],
      warning: primitives.orange[700],
      warningLight: primitives.orange[50],
      negative: primitives.red[600],
      negativeLight: primitives.red[50],
      disabled: primitives.grey[300],
      disabledLight: primitives.grey[100],
    },
  },
  border: {
    primary: primitives.navy[200],
    primaryDark: primitives.navy[400],
    secondary: primitives.navy[100],
    primaryInverse: primitives.white[100],
    accent: primitives.blue[500],
    accentLight: primitives.blue[50],
    extension: {
      positive: primitives.green[700],
      warning: primitives.orange[700],
      negative: primitives.red[700],
      disabled: primitives.grey[500],
    },
  },
  indicator: {
    onColor: primitives.white[100],
    grey: primitives.grey[600],
    violet: primitives.violet[600],
    magenta: primitives.magenta[600],
    navy: primitives.navy[600],
    blue: primitives.blue[500],
    cyan: primitives.cyan[700],
    green: primitives.green[700],
    lime: primitives.lime[600],
    yellow: primitives.yellow[700],
    orange: primitives.orange[700],
    red: primitives.red[600],
    greyLight: primitives.grey[200],
    violetLight: primitives.violet[50],
    magentaLight: primitives.magenta[50],
    navyLight: primitives.navy[50],
    blueLight: primitives.blue[100],
    cyanLight: primitives.cyan[100],
    greenLight: primitives.green[100],
    limeLight: primitives.lime[100],
    yellowLight: primitives.yellow[100],
    orangeLight: primitives.orange[100],
    redLight: primitives.red[100],
    greyDark: primitives.grey[900],
    violetDark: primitives.violet[800],
    magentaDark: primitives.magenta[800],
    navyDark: primitives.navy[800],
    blueDark: primitives.blue[500],
    cyanDark: primitives.cyan[800],
    greenDark: primitives.green[800],
    limeDark: primitives.lime[800],
    yellowDark: primitives.yellow[900],
    orangeDark: primitives.orange[800],
    redDark: primitives.red[800],
  },
  state: {
    light08: withOpacity(getPrimitiveColor(primitives.white, 100), 0.08),
    light12: withOpacity(getPrimitiveColor(primitives.white, 100), 0.12),
    light16: withOpacity(getPrimitiveColor(primitives.white, 100), 0.16),
    onColor08: withOpacity(getPrimitiveColor(primitives.white, 100), 0.08),
    onColor12: withOpacity(getPrimitiveColor(primitives.white, 100), 0.12),
    onColor16: withOpacity(getPrimitiveColor(primitives.white, 100), 0.16),
    onDark08: withOpacity(getPrimitiveColor(primitives.grey, 900), 0.08),
    onDark12: withOpacity(getPrimitiveColor(primitives.grey, 900), 0.12),
    onDark16: withOpacity(getPrimitiveColor(primitives.grey, 900), 0.16),
  },
  overlay: {
    primary60: withOpacity(getPrimitiveColor(primitives.black, 900), 0.6),
  },
};

export type SemanticIndicatorColors =
  | "grey"
  | "violet"
  | "magenta"
  | "navy"
  | "blue"
  | "cyan"
  | "green"
  | "lime"
  | "yellow"
  | "orange"
  | "red";

// Available color options from semantic theme
export const colorOptions: SemanticIndicatorColors[] = [
  "grey",
  "violet",
  "magenta",
  "navy",
  "blue",
  "cyan",
  "green",
  "lime",
  "yellow",
  "orange",
  "red",
];

// Color mapper function to convert rgba/hex colors to semantic colors
export const mapColorToSemantic = (color?: string): SemanticIndicatorColors => {
  // Define color mappings - you can adjust these based on your actual colors
  const colorMappings: Record<string, SemanticIndicatorColors> = {
    // Add your specific rgba colors here
    // TODO: get the colors from the status
    "rgba(104,87,87,0.7)": "grey",
    "rgba(54,162,235,0.7)": "blue",
    "rgba(255,99,132,0.7)": "red",
    "rgba(9,114,31,0.7)": "green",
    "rgba(255,206,86,0.7)": "yellow",
    "rgba(153,102,255,0.7)": "violet",
    "rgba(255,159,64,0.7)": "orange",
    "rgba(255,99,255,0.7)": "magenta",
    "rgba(0,123,255,0.7)": "cyan",
    "rgba(50,205,50,0.7)": "lime",
    "rgba(25,25,112,0.7)": "navy",
    // Add more mappings as needed
  };

  // Handle undefined color
  if (!color) {
    return "blue";
  }

  // Clean up the color string (remove spaces)
  const cleanColor = color.replace(/\s/g, "");

  // Check if we have a direct mapping
  if (colorMappings[cleanColor]) {
    return colorMappings[cleanColor];
  }

  // If it's a hex color, you can add logic to map hex to semantic colors
  if (color.startsWith("#")) {
    // Add hex color mapping logic here if needed
    return "blue"; // default fallback
  }

  // If it's an rgba color, you can parse it and map based on RGB values
  if (color.startsWith("rgba")) {
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      const [, r, g, b] = rgbaMatch.map(Number);

      // Simple color detection based on dominant RGB values
      if (r > g && r > b && r > 150) return "red";
      if (g > r && g > b && g > 150) return "green";
      if (b > r && b > g && b > 150) return "blue";
      if (r > 150 && g > 150 && b < 100) return "yellow";
      if (r > 150 && g < 100 && b > 150) return "violet";
      if (r > 150 && g > 100 && b < 100) return "orange";
      if (r < 100 && g < 100 && b < 100) return "grey";
      if (r > 100 && g > 100 && b > 100) return "grey";
    }
  }

  // Default fallback
  return "blue";
};
